import type { SalesAnalytics } from "../sales-context/SalesContext.types";

import { enrollmentStatusToSaleProgress } from "../api/enrollment";
import { useLanguageStore } from "../modules/product-sale-flow/components/language-selector/useLanguageStore";
import { isNoChecklistProduct } from "../modules/product-sale-flow/utils/isNoChecklistProduct";
import { useIntegratedFlowStore } from "../stores/useIntegratedFlowStore";
import { useProductOfferStore } from "../stores/useProductOfferStore";
import { SaleProgress } from "../types/types-sales";

export interface EnrollmentEventData {
    offerId: string;
    status: string;
    salesProvider: string;
}

/**
 * Handles an enrollment status event from the host's WebSocket.
 *
 * Usage in host (Workspace.tsx):
 *   import { handleEnrollmentEvent } from "@expert/sales-tools";
 *   expertWorkspaceWebSocketEventBus.on("ws_enrollment-event", (data) =>
 *       handleEnrollmentEvent(analytics, data)
 *   );
 */
export function handleEnrollmentEvent(analytics: SalesAnalytics, data: EnrollmentEventData): void {
    const { offerId, status, salesProvider } = data;
    const progress = enrollmentStatusToSaleProgress(status);
    if (!progress) return;

    const sessionId = useProductOfferStore.getState().getSessionIdForProductId(offerId);
    if (!sessionId) return;

    const primaryProducts = useProductOfferStore.getState().getPrimaryProducts(sessionId);
    if (!primaryProducts.some((p) => p.id === offerId)) return;

    useProductOfferStore.getState().updateProduct(sessionId, offerId, { saleProgress: progress });

    void analytics.dispatcher.dispatchBusinessEvent("EnrollmentEventReceived", {
        productOfferId: offerId,
        enrollmentStatus: status,
        sessionId,
        salesProvider,
    });

    // Keep this attribution identical to the sync createEnrollment path: DECLINED is a terminal,
    // expected outcome, not a failure.
    const fullStoryEvent =
        progress === SaleProgress.Success
            ? "EnrollmentSuccess"
            : progress === SaleProgress.Declined
              ? "EnrollmentDeclined"
              : "EnrollmentFailure";
    analytics.trackFullStoryEvent(fullStoryEvent, {
        productOfferId: offerId,
        sessionId,
        enrollmentStatus: status,
        statusOrigin: salesProvider,
    });

    if (useProductOfferStore.getState().isPrimaryProductInProgress(sessionId)) {
        // Re-show the guided offer screen (SuggestedOfferStep = FullOfferChecklistStep) so the
        // expert sees the outcome. That screen only exists for checklist-backed products: a
        // no-checklist *primary* skips SuggestedOfferStep entirely, so sending the session there
        // while such a product is selected would (a) trip IntegratedFlow's Back-detection guard
        // and deselect it mid-flow, and (b) get remapped back to its entry step, restarting the
        // sale. Its current step (e.g. WaitingForEnrollment) already reflects the saleProgress
        // updated above, so leave the step untouched for it. Mirror IntegratedFlow's own
        // `isNoChecklistProduct` check on the *selected* product so the two stay in lockstep.
        const store = useProductOfferStore.getState();
        const selected = store.getSelectedProduct(sessionId);
        const selectedInfo = selected
            ? store.getProductInfo(sessionId, selected.productSku, useLanguageStore.getState().language)
            : undefined;
        const selectedIsNoChecklistPrimary = !!selected?.isPrimaryProduct && isNoChecklistProduct(selectedInfo);

        if (!selectedIsNoChecklistPrimary) {
            useIntegratedFlowStore.getState().updateIntegratedStep(sessionId, "SuggestedOfferStep");
        }
    }
}
